import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScanBarcode, Check, X } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { returnsService } from '@/services/returns';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ReturnOrder } from '@/types/returns';

interface Props {
  record: ReturnOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ScanLog {
  id: number;
  ts: number;
  ok: boolean;
  text: string;
  detail?: string;
}

let LOG_KEY = 1;

/**
 * Scan-inward — receive units against a return. Two operating modes:
 *
 *   Barcode mode (default) — operator scans a finished-good barcode; the
 *   BE looks up which return-line it belongs to and bumps that line's
 *   `receivedQuantity` by 1. Logs each scan in the inline tape so the
 *   operator can verify a wedge scanner is sending the right code.
 *
 *   Manual mode — operator picks a specific return line and a quantity.
 *   Used when no scanner is available or the customer returned mixed
 *   units in a single bundle.
 *
 * The dialog stays open across scans so a high-volume operator can rip
 * through a stack of returns without dismissing between each one.
 */
export function ScanInwardDialog({ record, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<'barcode' | 'manual'>('barcode');
  const [barcode, setBarcode] = useState('');
  const [returnOrderItemId, setReturnOrderItemId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [log, setLog] = useState<ScanLog[]>([]);

  useEffect(() => {
    if (!open) return;
    setMode('barcode');
    setBarcode('');
    setReturnOrderItemId(record?.items[0]?.id ?? '');
    setQuantity('1');
    setLog([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, record]);

  const scan = useMutation({
    mutationFn: () => {
      if (mode === 'barcode') {
        return returnsService.scanInward(record!.id, { barcode: barcode.trim() });
      }
      return returnsService.scanInward(record!.id, {
        returnOrderItemId,
        quantity: Number(quantity),
      });
    },
    onSuccess: () => {
      const text = mode === 'barcode' ? barcode.trim() : `${quantity} unit(s)`;
      setLog((l) => [
        {
          id: LOG_KEY++,
          ts: Date.now(),
          ok: true,
          text,
          detail: mode === 'manual'
            ? record?.items.find((i) => i.id === returnOrderItemId)?.sku ?? undefined
            : undefined,
        },
        ...l.slice(0, 19),
      ]);
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['returns', 'detail', record?.id] });
      // Re-arm for the next scan.
      setBarcode('');
      setQuantity('1');
      setTimeout(() => inputRef.current?.focus(), 30);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Scan failed';
      setLog((l) => [
        {
          id: LOG_KEY++,
          ts: Date.now(),
          ok: false,
          text: mode === 'barcode' ? barcode.trim() || '(empty)' : `${quantity} unit(s)`,
          detail: message,
        },
        ...l.slice(0, 19),
      ]);
      toast.error(message);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!record) return;
    if (mode === 'barcode' && !barcode.trim()) return;
    if (mode === 'manual' && (!returnOrderItemId || !Number(quantity))) return;
    scan.mutate();
  };

  const successCount = log.filter((l) => l.ok).length;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <ScanBarcode className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              Scan inward · {record?.returnNumber}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              {successCount > 0 ? `Received ${successCount} unit${successCount === 1 ? '' : 's'}` : 'Receive returned units'}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Scan each unit's barcode (or manually pick a line + quantity). The
              dialog stays open so you can rip through a stack.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel={mode === 'barcode' ? 'Receive scan' : 'Receive units'}
      onSubmit={onSubmit}
      isSubmitting={scan.isPending}
      cancelLabel="Done"
      size="2xl"
    >
      {/* Mode picker */}
      <div className="grid grid-cols-2 gap-2">
        <ModeButton
          active={mode === 'barcode'}
          label="Barcode"
          hint="Scan one unit at a time"
          onClick={() => setMode('barcode')}
        />
        <ModeButton
          active={mode === 'manual'}
          label="Manual"
          hint="Line + quantity"
          onClick={() => setMode('manual')}
        />
      </div>

      {mode === 'barcode' ? (
        <FormField label="Barcode" htmlFor="scanInput" required>
          <Input
            id="scanInput"
            ref={inputRef}
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Wedge-scan or type and press Enter"
            className="h-9 font-mono"
            autoComplete="off"
          />
        </FormField>
      ) : (
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-8">
            <FormField label="Return line" htmlFor="returnOrderItemId" required>
              <Select value={returnOrderItemId} onValueChange={setReturnOrderItemId}>
                <SelectTrigger id="returnOrderItemId" className="h-9">
                  <SelectValue placeholder="Pick a line" />
                </SelectTrigger>
                <SelectContent>
                  {(record?.items ?? []).map((l) => {
                    const remaining = Number(l.quantity) - Number(l.receivedQuantity);
                    return (
                      <SelectItem key={l.id} value={l.id}>
                        <span>{l.name}</span>
                        <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                          {l.sku} · {fmt.number(remaining)} left
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="col-span-4">
            <FormField label="Qty" htmlFor="qty" required>
              <Input
                id="qty"
                ref={inputRef}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.001"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-9 text-right font-mono tabular-nums"
              />
            </FormField>
          </div>
        </div>
      )}

      {/* Per-line progress */}
      {record && record.items.length > 0 && (
        <div className="space-y-1 rounded-md border border-border/70 bg-card p-3">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Per-line progress
          </div>
          {record.items.map((l) => {
            const rec = Number(l.receivedQuantity);
            const claim = Number(l.quantity);
            const pct = claim > 0 ? Math.min(100, (rec / claim) * 100) : 0;
            return (
              <div key={l.id} className="flex items-center gap-3 text-[12px]">
                <div className="min-w-0 flex-1 truncate">
                  <span className="font-mono text-[10.5px] text-muted-foreground">{l.sku}</span>
                  <span className="ml-2">{l.name}</span>
                </div>
                <div className="font-mono tabular-nums text-muted-foreground">
                  {fmt.number(rec)} / {fmt.number(claim)}
                </div>
                <div className="w-16 overflow-hidden rounded bg-muted">
                  <div
                    className={cn(
                      'h-1 transition-all',
                      pct >= 100 ? 'bg-success' : 'bg-primary',
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scan log tape */}
      {log.length > 0 && (
        <div className="space-y-1 rounded-md border border-border/70 bg-muted/20 p-3">
          <div className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Scan log
          </div>
          <div className="space-y-1">
            {log.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-2 text-[11.5px]"
              >
                {entry.ok ? (
                  <Check className="h-3 w-3 shrink-0 text-success" />
                ) : (
                  <X className="h-3 w-3 shrink-0 text-destructive" />
                )}
                <span className="font-mono text-foreground">{entry.text}</span>
                {entry.detail && (
                  <Badge variant="muted" className="text-[10px]">
                    {entry.detail}
                  </Badge>
                )}
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </FormSheet>
  );
}

function ModeButton({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2 text-left transition-colors',
        active
          ? 'border-primary/30 bg-primary/[0.03] ring-1 ring-inset ring-primary/15'
          : 'border-border/70 bg-card hover:border-border',
      )}
    >
      <div className="text-[12.5px] font-medium text-foreground">{label}</div>
      <div className="text-[11px] text-muted-foreground">{hint}</div>
    </button>
  );
}

// React.forwardRef shim — react-hook-style refs into Input from shadcn.
declare module 'react' {
  interface InputHTMLAttributes<T> {
    ref?: React.Ref<T>;
  }
}

export {};
